import React, { useState, useRef, useEffect } from 'react'
import { 
  forgotPassword, 
  generateOTP,
  sendMsg91Otp,
  verifyMsg91Otp,
  retryMsg91Otp,
  forgotPasswordMsg91
} from '../apis/services/pgApi'

// OTP Input Component with dynamic length
const OTPInput = ({ value, onChange, length = 6, disabled = false }) => {
  const inputs = useRef([])

  const handleKey = (i, e) => {
    if (e.key === 'Backspace' && !e.target.value && i > 0) {
      inputs.current[i - 1]?.focus()
    }
  }

  const handleChange = (i, e) => {
    if (disabled) return
    const digit = e.target.value.replace(/\D/g, '').slice(-1)
    const arr = value.split('')
    arr[i] = digit
    const next = arr.join('').padEnd(length, ' ').slice(0, length)
    onChange(next.trimEnd())
    if (digit && i < length - 1) inputs.current[i + 1]?.focus()
  }

  const handlePaste = (e) => {
    if (disabled) return
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (paste) {
      onChange(paste)
      inputs.current[Math.min(paste.length, length - 1)]?.focus()
    }
    e.preventDefault()
  }

  return (
    <div className="otp-row">
      {Array.from({ length: length }, (_, i) => (
        <input
          key={i}
          ref={el => inputs.current[i] = el}
          type="text"
          inputMode="numeric"
          maxLength={1}
          className={`otp-box ${value[i] && value[i] !== ' ' ? 'filled' : ''} ${disabled ? 'disabled' : ''}`}
          value={value[i] && value[i] !== ' ' ? value[i] : ''}
          onChange={e => handleChange(i, e)}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
        />
      ))}
    </div>
  )
}

const ForgotPasswordModal = ({ onClose }) => {
  const overlayRef = useRef(null)
  const [step, setStep] = useState('form') // 'form', 'otp', 'password', 'reset'
  const [contact, setContact] = useState('')
  const [contactType, setContactType] = useState('mobile') // 'mobile' or 'email'
  const [otp, setOtp] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [generatedOTP, setGeneratedOTP] = useState('')
  const [otpVerified, setOtpVerified] = useState(false)
  const [otpLength, setOtpLength] = useState(4)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  const handleOverlayClick = (e) => { if (e.target === overlayRef.current) onClose() }

  // Format mobile number with +91
  const getFormattedMobile = (mobile) => {
    const cleaned = mobile.replace(/\D/g, '')
    if (cleaned.length === 10) {
      return `91${cleaned}`
    }
    return cleaned
  }

  // Reset everything to form step
  const resetToForm = () => {
    setStep('form')
    setError('')
    setOtp('')
    setOtpVerified(false)
    setNewPassword('')
    setConfirmPassword('')
    setLoading(false)
  }

  // Handle Send OTP
  const handleSendOTP = async () => {
    if (!contact.trim()) {
      setError('Please enter mobile number or email address')
      return
    }

    // Validate based on contact type
    if (contactType === 'mobile') {
      if (!/^\d{10}$/.test(contact.trim())) {
        setError('Please enter a valid 10-digit mobile number')
        return
      }
    } else {
      if (!/\S+@\S+\.\S+/.test(contact.trim())) {
        setError('Please enter a valid email address')
        return
      }
    }

    setLoading(true)
    setError('')

    try {
      if (contactType === 'mobile') {
        // MOBILE: Send OTP via MSG91 (4-digit OTP)
        const mobileWithCountryCode = getFormattedMobile(contact.trim())
        
        const otpData = {
          mobile: mobileWithCountryCode,
          authkey: '522317Abg4sY6n6a2fb1ebP1',
          template_id: '6a35297075006cf37e02f3f2',
          param1: 'OTP',
          param2: 'Forgot Password',
          param3: 'PG'
        }

        console.log('Calling MSG91 send OTP with:', otpData)
        const response = await sendMsg91Otp(otpData)
        console.log('MSG91 Send OTP response:', response)

        if (response && response.success) {
          setOtpLength(4)
          setStep('otp')
          setOtp('')
          setOtpVerified(false)
          setError('')
        } else {
          setError(response.message || 'Failed to send OTP. Please try again.')
        }
      } else {
        // EMAIL: Send OTP via generateOTP (4-digit OTP)
        const otpData = {
          MobileNumber: contact.trim(),
          Purpose: 'FORGOT_PASSWORD',
          PGID: null,
          ReferenceID: null
        }

        console.log('Calling generate OTP for email with:', otpData)
        const response = await generateOTP(otpData)
        console.log('Generate OTP response:', response)

        if (response && response.OTPCode) {
          setGeneratedOTP(response.OTPCode)
          console.log('OTP Generated:', response.OTPCode)
          setOtpLength(4)
          setStep('otp')
          setOtp('')
          setOtpVerified(false)
          setError('')
        } else {
          setError('Failed to send OTP. Please try again.')
        }
      }
    } catch (err) {
      console.error('Error sending OTP:', err)
      setError(err.message || 'Failed to send OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Handle Verify OTP for Mobile
  const handleVerifyMobileOTP = async () => {
    const cleanedOTP = otp.replace(/\s/g, '')
    
    // if (cleanedOTP.length !== 4) {
    //   setError('Please enter the 4-digit OTP')
    //   return
    // }

    setLoading(true)
    setError('')

    try {
      const mobileWithCountryCode = getFormattedMobile(contact.trim())
      
      console.log('Verifying MSG91 OTP for:', mobileWithCountryCode, 'OTP:', cleanedOTP)
      const response = await verifyMsg91Otp(mobileWithCountryCode, cleanedOTP)
      console.log('MSG91 Verify response:', response)

      if (response && response.success) {
        setOtpVerified(true)
        setStep('password') // Move to password step
        setError('')
      } else {
        setError(response.message || 'Invalid OTP. Please try again.')
      }
    } catch (err) {
      console.error('Error verifying OTP:', err)
      setError(err.message || 'OTP verification failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Handle Reset Password for Mobile
  const handleResetPasswordMobile = async () => {
    // Validate passwords
    if (!newPassword.trim()) {
      setError('Please enter new password')
      return
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)
    setError('')

    try {
      const resetData = {
        MobileNo: contact.trim(),
        EmailID: null,
        OTPVerifyByMSG91: 'verified',
        NewPassword: newPassword
      }

      console.log('Calling forgot password MSG91 with:', resetData)
      const response = await forgotPasswordMsg91(resetData)
      console.log('Forgot password MSG91 response:', response)

      if (response && response.success) {
        setStep('reset')
        setError('')
      } else {
        setError(response.message || 'Failed to reset password. Please try again.')
      }
    } catch (err) {
      console.error('Error resetting password:', err)
      setError(err.message || 'Failed to reset password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Handle Reset Password for Email (verifies OTP and updates password in one call)
  const handleResetPasswordEmail = async () => {
    const cleanedOTP = otp.replace(/\s/g, '')
    
    // Validate OTP
    // if (cleanedOTP.length !== 4) {
    //   setError('Please enter the 4-digit OTP')
    //   return
    // }

    // Validate passwords
    if (!newPassword.trim()) {
      setError('Please enter new password')
      return
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)
    setError('')

    try {
      const resetData = {
        MobileNo: null,
        EmailID: contact.trim(),
        OTP: cleanedOTP,
        NewPassword: newPassword
      }

      console.log('Calling forgot password for email with:', resetData)
      const response = await forgotPassword(resetData)
      console.log('Forgot password email response:', response)

      if (response && response.success) {
        setStep('reset')
        setError('')
      } else {
        setError(response.message || 'Failed to reset password. Please try again.')
      }
    } catch (err) {
      console.error('Error resetting password:', err)
      setError(err.message || 'Failed to reset password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Handle Reset Password (routes to appropriate function based on contact type)
  const handleResetPassword = () => {
    if (contactType === 'mobile') {
      handleResetPasswordMobile()
    } else {
      handleResetPasswordEmail()
    }
  }

  // Resend OTP
  const handleResendOTP = async () => {
    setLoading(true)
    setError('')

    try {
      if (contactType === 'mobile') {
        // MOBILE: Resend OTP via MSG91
        const mobileWithCountryCode = getFormattedMobile(contact.trim())
        const response = await retryMsg91Otp(mobileWithCountryCode, 'text')
        console.log('MSG91 Retry response:', response)

        if (response && response.success) {
          setOtp('')
          setOtpVerified(false)
          setStep('otp')
          setError('OTP resent successfully!')
          setTimeout(() => setError(''), 3000)
        } else {
          setError(response.message || 'Failed to resend OTP. Please try again.')
        }
      } else {
        // EMAIL: Resend OTP via generateOTP
        const otpData = {
          MobileNumber: contact.trim(),
          Purpose: 'FORGOT_PASSWORD',
          PGID: null,
          ReferenceID: null
        }

        const response = await generateOTP(otpData)
        console.log('OTP Resent:', response)

        if (response && response.OTPCode) {
          setGeneratedOTP(response.OTPCode)
          setOtp('')
          setOtpVerified(false)
          setStep('otp')
          setError('OTP resent successfully!')
          setTimeout(() => setError(''), 3000)
        } else {
          setError('Failed to resend OTP. Please try again.')
        }
      }
    } catch (err) {
      console.error('Error resending OTP:', err)
      setError('Failed to resend OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Handle OTP input change
  const handleOtpChange = (value) => {
    setOtp(value)
    setError('')
    
    // Auto-verify only for mobile when OTP is complete
    // if (contactType === 'mobile') {
    //   const cleanedOTP = value.replace(/\s/g, '')
    //   if (cleanedOTP.length === 4 && !otpVerified) {
    //     setTimeout(() => {
    //       handleVerifyMobileOTP()
    //     }, 500)
    //   }
    // }
    // For email, no auto-verify - user fills OTP and password together
  }

  // Get OTP length display message
  const getOTPLengthMessage = () => {
    return '4-digit'
  }

  // Handle back button click
  const handleBack = () => {
    if (step === 'form') {
      onClose()
    } else if (step === 'otp') {
      resetToForm()
    } else if (step === 'password') {
      resetToForm()
    } else if (step === 'reset') {
      resetToForm()
    }
  }

  // Check if OTP is complete (4 digits)
  const isOtpComplete = () => {
    return otp.replace(/\s/g, '').length === 4
  }

  return (
    <div className="auth-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="auth-modal">
        <button className="auth-close" onClick={onClose}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Back button - goes back to form step */}
        {step !== 'form' && (
          <button className="auth-back" onClick={handleBack}>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M13 4L7 10L13 16" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Back
          </button>
        )}

        <div className="auth-header">
          {step === 'form' && (
            <>
              <h2>Forgot Password?</h2>
              <p>Enter your registered mobile number or email to reset password</p>
            </>
          )}
          {step === 'otp' && (
            <>
              <h2>Enter OTP</h2>
              <p>We've sent a {getOTPLengthMessage()} verification code to your {contactType}</p>
              {contactType === 'mobile' && (
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  OTP will be auto-verified when you complete all 4 digits
                </p>
              )}
              {contactType === 'email' && (
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Enter the OTP and set your new password below
                </p>
              )}
            </>
          )}
          {step === 'password' && (
            <>
              <h2>✓ OTP Verified!</h2>
              <p>Now set your new password</p>
            </>
          )}
          {step === 'reset' && (
            <>
              <h2>Password Reset Successfully!</h2>
              <p>You can now login with your new password</p>
            </>
          )}
        </div>

        {/* Step 1: Contact Input */}
        {step === 'form' && (
          <div className="auth-form">
            <div className="auth-type-toggle" style={{ marginBottom: '20px' }}>
              <button 
                className={contactType === 'mobile' ? 'active' : ''} 
                onClick={() => {
                  setContactType('mobile')
                  setContact('')
                  setError('')
                }}
              >
                Using Mobile Number
              </button>
              <button 
                className={contactType === 'email' ? 'active' : ''} 
                onClick={() => {
                  setContactType('email')
                  setContact('')
                  setError('')
                }}
              >
                Using Email ID
              </button>
            </div>

            <div className="auth-field">
              <label>{contactType === 'mobile' ? 'Mobile Number' : 'Email Address'}</label>
              {contactType === 'mobile' ? (
                <div className="auth-phone-row">
                  <span className="auth-phone-prefix">+91</span>
                  <input 
                    type="tel" 
                    placeholder="Enter 10-digit mobile number" 
                    value={contact} 
                    onChange={e => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10)
                      setContact(value)
                      setError('')
                    }} 
                    maxLength={10} 
                    autoFocus
                  />
                </div>
              ) : (
                <input 
                  type="email" 
                  placeholder="you@example.com" 
                  value={contact} 
                  onChange={e => {
                    setContact(e.target.value)
                    setError('')
                  }} 
                  autoFocus
                />
              )}
            </div>

            {error && <p className="auth-error">{error}</p>}
            
            <button 
              className="auth-submit" 
              onClick={handleSendOTP} 
              disabled={loading}
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </div>
        )}

        {/* Step 2: OTP Input */}
        {step === 'otp' && (
          <div className="auth-otp-section">
            <p className="auth-otp-hint" style={{ textAlign: 'center', marginBottom: '15px' }}>
              Enter the {getOTPLengthMessage()} verification code sent to your {contactType}
            </p>
            
            <OTPInput 
              value={otp} 
              onChange={handleOtpChange} 
              length={4}
              disabled={loading || otpVerified}
            />
            
            {loading && (
              <p style={{ color: '#6366F1', textAlign: 'center', marginTop: '10px', fontSize: '14px' }}>
                {contactType === 'mobile' ? 'Verifying OTP...' : 'Processing...'}
              </p>
            )}

            {/* For Mobile: Show verify button (fallback if auto-verify fails) */}
            {contactType === 'mobile' && !otpVerified && !loading && isOtpComplete() && (
              <button 
                className="auth-submit" 
                onClick={handleVerifyMobileOTP} 
                disabled={loading}
                style={{ marginTop: '15px', backgroundColor: '#6366F1' }}
              >
                Verify OTP
              </button>
            )}
            
            <p className="auth-otp-hint" style={{ marginTop: '15px' }}>
              Didn't receive it?{' '}
              <span className="auth-resend" onClick={handleResendOTP}>
                Resend Code
              </span>
            </p>

            {/* For Email: Show password fields alongside OTP */}
            {contactType === 'email' && (
              <div className="auth-otp-section" style={{ marginTop: '20px', borderTop: '1px solid #E5E7EB', paddingTop: '20px' }}>
                {/* <div style={{ 
                  marginBottom: '15px',
                  padding: '8px 12px',
                  backgroundColor: '#F3F4F6',
                  borderRadius: '6px',
                  color: '#6B7280',
                  fontSize: '13px',
                  textAlign: 'center'
                }}>
                  Enter OTP above and set new password below
                </div> */}

                <div className="auth-field">
                  <label>New Password</label>
                  <input 
                    type="password" 
                    placeholder="Enter new password (min 6 characters)" 
                    value={newPassword} 
                    onChange={e => {
                      setNewPassword(e.target.value)
                      setError('')
                    }} 
                  />
                </div>

                <div className="auth-field">
                  <label>Confirm New Password</label>
                  <input 
                    type="password" 
                    placeholder="Confirm your new password" 
                    value={confirmPassword} 
                    onChange={e => {
                      setConfirmPassword(e.target.value)
                      setError('')
                    }} 
                  />
                </div>

                {error && <p className="auth-error">{error}</p>}
                
                <button 
                  className="auth-submit" 
                  onClick={handleResetPasswordEmail} 
                  disabled={loading || !isOtpComplete()}
                  style={{ marginTop: '10px' }}
                >
                  {loading ? 'Resetting Password...' : 'Reset Password'}
                </button>
              </div>
            )}

            {/* For Mobile: Show a message that OTP verified, will go to password step */}
            {contactType === 'mobile' && otpVerified && step === 'otp' && (
              <div style={{ 
                textAlign: 'center', 
                marginTop: '20px',
                padding: '10px',
                backgroundColor: '#DCFCE7',
                borderRadius: '8px',
                color: '#16A34A'
              }}>
                ✓ OTP verified! Redirecting to set new password...
              </div>
            )}

            {error && contactType === 'mobile' && <p className="auth-error" style={{ marginTop: '15px' }}>{error}</p>}
          </div>
        )}

        {/* Step 3: New Password (only for Mobile) */}
        {step === 'password' && contactType === 'mobile' && (
          <div className="auth-otp-section">
            <div style={{ 
              textAlign: 'center', 
              marginBottom: '20px',
              padding: '10px',
              backgroundColor: '#DCFCE7',
              borderRadius: '8px',
              color: '#16A34A'
            }}>
              ✓ OTP verified successfully!
            </div>

            <div className="auth-field">
              <label>New Password</label>
              <input 
                type="password" 
                placeholder="Enter new password (min 6 characters)" 
                value={newPassword} 
                onChange={e => {
                  setNewPassword(e.target.value)
                  setError('')
                }} 
                autoFocus
              />
            </div>

            <div className="auth-field">
              <label>Confirm New Password</label>
              <input 
                type="password" 
                placeholder="Confirm your new password" 
                value={confirmPassword} 
                onChange={e => {
                  setConfirmPassword(e.target.value)
                  setError('')
                }} 
              />
            </div>

            {error && <p className="auth-error">{error}</p>}
            
            <button 
              className="auth-submit" 
              onClick={handleResetPasswordMobile} 
              disabled={loading}
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </div>
        )}

        {/* Step 4: Success Message */}
        {step === 'reset' && (
          <div className="auth-otp-section">
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="32" fill="#DCFCE7" />
                <path d="M20 32L28 40L44 24" stroke="#16A34A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            
            <p style={{ textAlign: 'center', marginBottom: '20px', color: '#10B981' }}>
              Your password has been successfully reset!
            </p>
            
            <button className="auth-submit" onClick={onClose}>
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default ForgotPasswordModal

// import React, { useState, useRef, useEffect } from 'react'
// import { 
//   forgotPassword, 
//   generateOTP,
//   sendMsg91Otp,
//   verifyMsg91Otp,
//   retryMsg91Otp,
//   forgotPasswordMsg91
// } from '../apis/services/pgApi'

// // OTP Input Component with dynamic length
// const OTPInput = ({ value, onChange, length = 6, disabled = false }) => {
//   const inputs = useRef([])

//   const handleKey = (i, e) => {
//     if (e.key === 'Backspace' && !e.target.value && i > 0) {
//       inputs.current[i - 1]?.focus()
//     }
//   }

//   const handleChange = (i, e) => {
//     if (disabled) return
//     const digit = e.target.value.replace(/\D/g, '').slice(-1)
//     const arr = value.split('')
//     arr[i] = digit
//     const next = arr.join('').padEnd(length, ' ').slice(0, length)
//     onChange(next.trimEnd())
//     if (digit && i < length - 1) inputs.current[i + 1]?.focus()
//   }

//   const handlePaste = (e) => {
//     if (disabled) return
//     const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
//     if (paste) {
//       onChange(paste)
//       inputs.current[Math.min(paste.length, length - 1)]?.focus()
//     }
//     e.preventDefault()
//   }

//   return (
//     <div className="otp-row">
//       {Array.from({ length: length }, (_, i) => (
//         <input
//           key={i}
//           ref={el => inputs.current[i] = el}
//           type="text"
//           inputMode="numeric"
//           maxLength={1}
//           className={`otp-box ${value[i] && value[i] !== ' ' ? 'filled' : ''} ${disabled ? 'disabled' : ''}`}
//           value={value[i] && value[i] !== ' ' ? value[i] : ''}
//           onChange={e => handleChange(i, e)}
//           onKeyDown={e => handleKey(i, e)}
//           onPaste={handlePaste}
//           disabled={disabled}
//         />
//       ))}
//     </div>
//   )
// }

// const ForgotPasswordModal = ({ onClose }) => {
//   const overlayRef = useRef(null)
//   const [step, setStep] = useState('form') // 'form', 'otp', 'password', 'reset'
//   const [contact, setContact] = useState('')
//   const [contactType, setContactType] = useState('mobile') // 'mobile' or 'email'
//   const [otp, setOtp] = useState('')
//   const [newPassword, setNewPassword] = useState('')
//   const [confirmPassword, setConfirmPassword] = useState('')
//   const [error, setError] = useState('')
//   const [loading, setLoading] = useState(false)
//   const [generatedOTP, setGeneratedOTP] = useState('')
//   const [otpVerified, setOtpVerified] = useState(false)
//   const [otpLength, setOtpLength] = useState(4)
//   // For email: track if OTP is being verified
//   const [isEmailOtpVerified, setIsEmailOtpVerified] = useState(false)

//   useEffect(() => {
//     document.body.style.overflow = 'hidden'
//     return () => { document.body.style.overflow = '' }
//   }, [])

//   useEffect(() => {
//     const h = (e) => { if (e.key === 'Escape') onClose() }
//     window.addEventListener('keydown', h)
//     return () => window.removeEventListener('keydown', h)
//   }, [onClose])

//   const handleOverlayClick = (e) => { if (e.target === overlayRef.current) onClose() }

//   // Format mobile number with +91
//   const getFormattedMobile = (mobile) => {
//     const cleaned = mobile.replace(/\D/g, '')
//     if (cleaned.length === 10) {
//       return `91${cleaned}`
//     }
//     return cleaned
//   }

//   // Reset everything to form step
//   const resetToForm = () => {
//     setStep('form')
//     setError('')
//     setOtp('')
//     setOtpVerified(false)
//     setIsEmailOtpVerified(false)
//     setNewPassword('')
//     setConfirmPassword('')
//     setLoading(false)
//   }

//   // Handle Send OTP
//   const handleSendOTP = async () => {
//     if (!contact.trim()) {
//       setError('Please enter mobile number or email address')
//       return
//     }

//     // Validate based on contact type
//     if (contactType === 'mobile') {
//       if (!/^\d{10}$/.test(contact.trim())) {
//         setError('Please enter a valid 10-digit mobile number')
//         return
//       }
//     } else {
//       if (!/\S+@\S+\.\S+/.test(contact.trim())) {
//         setError('Please enter a valid email address')
//         return
//       }
//     }

//     setLoading(true)
//     setError('')

//     try {
//       if (contactType === 'mobile') {
//         // MOBILE: Send OTP via MSG91 (4-digit OTP)
//         const mobileWithCountryCode = getFormattedMobile(contact.trim())
        
//         const otpData = {
//           mobile: mobileWithCountryCode,
//           authkey: '522317Abg4sY6n6a2fb1ebP1',
//           template_id: '6a35297075006cf37e02f3f2',
//           param1: 'OTP',
//           param2: 'Forgot Password',
//           param3: 'PG'
//         }

//         console.log('Calling MSG91 send OTP with:', otpData)
//         const response = await sendMsg91Otp(otpData)
//         console.log('MSG91 Send OTP response:', response)

//         if (response && response.success) {
//           setOtpLength(4)
//           setStep('otp')
//           setOtp('')
//           setOtpVerified(false)
//           setError('')
//         } else {
//           setError(response.message || 'Failed to send OTP. Please try again.')
//         }
//       } else {
//         // EMAIL: Send OTP via generateOTP (4-digit OTP)
//         const otpData = {
//           MobileNumber: contact.trim(),
//           Purpose: 'FORGOT_PASSWORD',
//           PGID: null,
//           ReferenceID: null
//         }

//         console.log('Calling generate OTP for email with:', otpData)
//         const response = await generateOTP(otpData)
//         console.log('Generate OTP response:', response)

//         if (response && response.OTPCode) {
//           setGeneratedOTP(response.OTPCode)
//           console.log('OTP Generated:', response.OTPCode)
//           setOtpLength(4)
//           setStep('otp')
//           setOtp('')
//           setOtpVerified(false)
//           setIsEmailOtpVerified(false)
//           setError('')
//         } else {
//           setError('Failed to send OTP. Please try again.')
//         }
//       }
//     } catch (err) {
//       console.error('Error sending OTP:', err)
//       setError(err.message || 'Failed to send OTP. Please try again.')
//     } finally {
//       setLoading(false)
//     }
//   }

//   // Handle Verify OTP for Mobile
//   const handleVerifyMobileOTP = async () => {
//     const cleanedOTP = otp.replace(/\s/g, '')
    
//     if (cleanedOTP.length !== 4) {
//       setError('Please enter the 4-digit OTP')
//       return
//     }

//     setLoading(true)
//     setError('')

//     try {
//       const mobileWithCountryCode = getFormattedMobile(contact.trim())
      
//       console.log('Verifying MSG91 OTP for:', mobileWithCountryCode, 'OTP:', cleanedOTP)
//       const response = await verifyMsg91Otp(mobileWithCountryCode, cleanedOTP)
//       console.log('MSG91 Verify response:', response)

//       if (response && response.success) {
//         setOtpVerified(true)
//         setStep('password') // Move to password step
//         setError('')
//       } else {
//         setError(response.message || 'Invalid OTP. Please try again.')
//       }
//     } catch (err) {
//       console.error('Error verifying OTP:', err)
//       setError(err.message || 'OTP verification failed. Please try again.')
//     } finally {
//       setLoading(false)
//     }
//   }

//   // Handle Verify OTP for Email (just shows password fields, doesn't call separate verify API)
//   const handleVerifyEmailOTP = () => {
//     const cleanedOTP = otp.replace(/\s/g, '')
    
//     if (cleanedOTP.length !== 4) {
//       setError('Please enter the 4-digit OTP')
//       return
//     }

//     // For email, we don't have a separate verify API
//     // The OTP will be verified during forgotPassword call
//     // Just show the password fields
//     setIsEmailOtpVerified(true)
//     setError('')
//   }

//   // Handle Reset Password for Mobile
//   const handleResetPasswordMobile = async () => {
//     // Validate passwords
//     if (!newPassword.trim()) {
//       setError('Please enter new password')
//       return
//     }

//     if (newPassword.length < 6) {
//       setError('Password must be at least 6 characters long')
//       return
//     }

//     if (newPassword !== confirmPassword) {
//       setError('Passwords do not match')
//       return
//     }

//     setLoading(true)
//     setError('')

//     try {
//       const resetData = {
//         MobileNo: contact.trim(),
//         EmailID: null,
//         OTPVerifyByMSG91: 'verified',
//         NewPassword: newPassword
//       }

//       console.log('Calling forgot password MSG91 with:', resetData)
//       const response = await forgotPasswordMsg91(resetData)
//       console.log('Forgot password MSG91 response:', response)

//       if (response && response.success) {
//         setStep('reset')
//         setError('')
//       } else {
//         setError(response.message || 'Failed to reset password. Please try again.')
//       }
//     } catch (err) {
//       console.error('Error resetting password:', err)
//       setError(err.message || 'Failed to reset password. Please try again.')
//     } finally {
//       setLoading(false)
//     }
//   }

//   // Handle Reset Password for Email (verifies OTP and updates password in one call)
//   const handleResetPasswordEmail = async () => {
//     const cleanedOTP = otp.replace(/\s/g, '')
    
//     // Validate OTP
//     if (cleanedOTP.length !== 4) {
//       setError('Please enter the 4-digit OTP')
//       return
//     }

//     // Validate passwords
//     if (!newPassword.trim()) {
//       setError('Please enter new password')
//       return
//     }

//     if (newPassword.length < 6) {
//       setError('Password must be at least 6 characters long')
//       return
//     }

//     if (newPassword !== confirmPassword) {
//       setError('Passwords do not match')
//       return
//     }

//     setLoading(true)
//     setError('')

//     try {
//       const resetData = {
//         MobileNo: null,
//         EmailID: contact.trim(),
//         OTP: cleanedOTP,
//         NewPassword: newPassword
//       }

//       console.log('Calling forgot password for email with:', resetData)
//       const response = await forgotPassword(resetData)
//       console.log('Forgot password email response:', response)

//       if (response && response.success) {
//         setStep('reset')
//         setError('')
//       } else {
//         setError(response.message || 'Failed to reset password. Please try again.')
//       }
//     } catch (err) {
//       console.error('Error resetting password:', err)
//       setError(err.message || 'Failed to reset password. Please try again.')
//     } finally {
//       setLoading(false)
//     }
//   }

//   // Handle Reset Password (routes to appropriate function based on contact type)
//   const handleResetPassword = () => {
//     if (contactType === 'mobile') {
//       handleResetPasswordMobile()
//     } else {
//       handleResetPasswordEmail()
//     }
//   }

//   // Resend OTP
//   const handleResendOTP = async () => {
//     setLoading(true)
//     setError('')

//     try {
//       if (contactType === 'mobile') {
//         // MOBILE: Resend OTP via MSG91
//         const mobileWithCountryCode = getFormattedMobile(contact.trim())
//         const response = await retryMsg91Otp(mobileWithCountryCode, 'text')
//         console.log('MSG91 Retry response:', response)

//         if (response && response.success) {
//           setOtp('')
//           setOtpVerified(false)
//           setStep('otp')
//           setError('OTP resent successfully!')
//           setTimeout(() => setError(''), 3000)
//         } else {
//           setError(response.message || 'Failed to resend OTP. Please try again.')
//         }
//       } else {
//         // EMAIL: Resend OTP via generateOTP
//         const otpData = {
//           MobileNumber: contact.trim(),
//           Purpose: 'FORGOT_PASSWORD',
//           PGID: null,
//           ReferenceID: null
//         }

//         const response = await generateOTP(otpData)
//         console.log('OTP Resent:', response)

//         if (response && response.OTPCode) {
//           setGeneratedOTP(response.OTPCode)
//           setOtp('')
//           setOtpVerified(false)
//           setIsEmailOtpVerified(false)
//           setStep('otp')
//           setError('OTP resent successfully!')
//           setTimeout(() => setError(''), 3000)
//         } else {
//           setError('Failed to resend OTP. Please try again.')
//         }
//       }
//     } catch (err) {
//       console.error('Error resending OTP:', err)
//       setError('Failed to resend OTP. Please try again.')
//     } finally {
//       setLoading(false)
//     }
//   }

//   // Handle OTP input change
//   const handleOtpChange = (value) => {
//     setOtp(value)
//     setError('')
    
//     // Don't auto-verify for either mobile or email
//     // User must click the Verify button
//   }

//   // Get OTP length display message
//   const getOTPLengthMessage = () => {
//     return '4-digit'
//   }

//   // Handle back button click
//   const handleBack = () => {
//     if (step === 'form') {
//       onClose()
//     } else if (step === 'otp') {
//       resetToForm()
//     } else if (step === 'password') {
//       resetToForm()
//     } else if (step === 'reset') {
//       resetToForm()
//     }
//   }

//   // Check if OTP is complete (4 digits)
//   const isOtpComplete = () => {
//     return otp.replace(/\s/g, '').length === 4
//   }

//   return (
//     <div className="auth-overlay" ref={overlayRef} onClick={handleOverlayClick}>
//       <div className="auth-modal">
//         <button className="auth-close" onClick={onClose}>
//           <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
//             <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
//           </svg>
//         </button>

//         {/* Back button - goes back to form step */}
//         {step !== 'form' && (
//           <button className="auth-back" onClick={handleBack}>
//             <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
//               <path d="M13 4L7 10L13 16" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
//             </svg>
//             Back
//           </button>
//         )}

//         <div className="auth-header">
//           {step === 'form' && (
//             <>
//               <h2>Forgot Password?</h2>
//               <p>Enter your registered mobile number or email to reset password</p>
//               {/* <p>Enter your registered mobile number to reset password</p> */}
//             </>
//           )}
//           {step === 'otp' && (
//             <>
//               <h2>Enter OTP</h2>
//               <p>We've sent a {getOTPLengthMessage()} verification code to your {contactType}</p>
//               <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
//                 Enter the OTP and click Verify to proceed
//               </p>
//             </>
//           )}
//           {step === 'password' && (
//             <>
//               <h2>✓ OTP Verified!</h2>
//               <p>Now set your new password</p>
//             </>
//           )}
//           {step === 'reset' && (
//             <>
//               <h2>Password Reset Successfully!</h2>
//               <p>You can now login with your new password</p>
//             </>
//           )}
//         </div>

//         {/* Step 1: Contact Input */}
//         {step === 'form' && (
//           <div className="auth-form">
//             <div className="auth-type-toggle" style={{ marginBottom: '20px' }}>
//               <button 
//                 className={contactType === 'mobile' ? 'active' : ''} 
//                 onClick={() => {
//                   setContactType('mobile')
//                   setContact('')
//                   setError('')
//                 }}
//               >
//                 Using Mobile Number
//               </button>
//               <button 
//                 className={contactType === 'email' ? 'active' : ''} 
//                 onClick={() => {
//                   setContactType('email')
//                   setContact('')
//                   setError('')
//                 }}
//               >
//                 Using Email ID
//               </button>
//             </div>

//             <div className="auth-field">
//               <label>{contactType === 'mobile' ? 'Mobile Number' : 'Email Address'}</label>
//               {contactType === 'mobile' ? (
//                 <div className="auth-phone-row">
//                   <span className="auth-phone-prefix">+91</span>
//                   <input 
//                     type="tel" 
//                     placeholder="Enter 10-digit mobile number" 
//                     value={contact} 
//                     onChange={e => {
//                       const value = e.target.value.replace(/\D/g, '').slice(0, 10)
//                       setContact(value)
//                       setError('')
//                     }} 
//                     maxLength={10} 
//                     autoFocus
//                   />
//                 </div>
//               ) : (
//                 <input 
//                   type="email" 
//                   placeholder="you@example.com" 
//                   value={contact} 
//                   onChange={e => {
//                     setContact(e.target.value)
//                     setError('')
//                   }} 
//                   autoFocus
//                 />
//               )}
//             </div>

//             {error && <p className="auth-error">{error}</p>}
            
//             <button 
//               className="auth-submit" 
//               onClick={handleSendOTP} 
//               disabled={loading}
//             >
//               {loading ? 'Sending OTP...' : 'Send OTP'}
//             </button>
//           </div>
//         )}

//         {/* Step 2: OTP Input */}
//         {step === 'otp' && (
//           <div className="auth-otp-section">
//             <p className="auth-otp-hint" style={{ textAlign: 'center', marginBottom: '15px' }}>
//               Enter the {getOTPLengthMessage()} verification code sent to your {contactType}
//             </p>
            
//             <OTPInput 
//               value={otp} 
//               onChange={handleOtpChange} 
//               length={4}
//               disabled={loading || otpVerified || (contactType === 'email' && isEmailOtpVerified)}
//             />
            
//             {loading && (
//               <p style={{ color: '#6366F1', textAlign: 'center', marginTop: '10px', fontSize: '14px' }}>
//                 Verifying OTP...
//               </p>
//             )}
            
//             <p className="auth-otp-hint" style={{ marginTop: '15px' }}>
//               Didn't receive it?{' '}
//               <span className="auth-resend" onClick={handleResendOTP}>
//                 Resend Code
//               </span>
//             </p>

//             {/* Verify Button - Shows when OTP is complete and not yet verified */}
//             {!otpVerified && !isEmailOtpVerified && !loading && isOtpComplete() && (
//               <button 
//                 className="auth-submit" 
//                 onClick={() => {
//                   if (contactType === 'mobile') {
//                     handleVerifyMobileOTP()
//                   } else {
//                     handleVerifyEmailOTP()
//                   }
//                 }} 
//                 disabled={loading}
//                 style={{ marginTop: '15px', backgroundColor: '#6366F1' }}
//               >
//                 Verify OTP
//               </button>
//             )}

//             {/* For Email: Show password fields after OTP verification */}
//             {contactType === 'email' && isEmailOtpVerified && step === 'otp' && (
//               <div className="auth-otp-section" style={{ marginTop: '20px', borderTop: '1px solid #E5E7EB', paddingTop: '20px' }}>
//                 <div style={{ 
//                   textAlign: 'center', 
//                   marginBottom: '20px',
//                   padding: '10px',
//                   backgroundColor: '#DCFCE7',
//                   borderRadius: '8px',
//                   color: '#16A34A'
//                 }}>
//                   ✓ OTP verified! Set your new password below
//                 </div>

//                 <div className="auth-field">
//                   <label>New Password</label>
//                   <input 
//                     type="password" 
//                     placeholder="Enter new password (min 6 characters)" 
//                     value={newPassword} 
//                     onChange={e => {
//                       setNewPassword(e.target.value)
//                       setError('')
//                     }} 
//                     autoFocus
//                   />
//                 </div>

//                 <div className="auth-field">
//                   <label>Confirm New Password</label>
//                   <input 
//                     type="password" 
//                     placeholder="Confirm your new password" 
//                     value={confirmPassword} 
//                     onChange={e => {
//                       setConfirmPassword(e.target.value)
//                       setError('')
//                     }} 
//                   />
//                 </div>

//                 {error && <p className="auth-error">{error}</p>}
                
//                 <button 
//                   className="auth-submit" 
//                   onClick={handleResetPasswordEmail} 
//                   disabled={loading}
//                   style={{ marginTop: '10px' }}
//                 >
//                   {loading ? 'Resetting Password...' : 'Reset Password'}
//                 </button>
//               </div>
//             )}

//             {/* For Mobile: Show a message that OTP verified, will go to password step */}
//             {contactType === 'mobile' && otpVerified && step === 'otp' && (
//               <div style={{ 
//                 textAlign: 'center', 
//                 marginTop: '20px',
//                 padding: '10px',
//                 backgroundColor: '#DCFCE7',
//                 borderRadius: '8px',
//                 color: '#16A34A'
//               }}>
//                 ✓ OTP verified! Redirecting to set new password...
//               </div>
//             )}

//             {error && <p className="auth-error" style={{ marginTop: '15px' }}>{error}</p>}
//           </div>
//         )}

//         {/* Step 3: New Password (only for Mobile) */}
//         {step === 'password' && contactType === 'mobile' && (
//           <div className="auth-otp-section">
//             <div style={{ 
//               textAlign: 'center', 
//               marginBottom: '20px',
//               padding: '10px',
//               backgroundColor: '#DCFCE7',
//               borderRadius: '8px',
//               color: '#16A34A'
//             }}>
//               ✓ OTP verified successfully!
//             </div>

//             <div className="auth-field">
//               <label>New Password</label>
//               <input 
//                 type="password" 
//                 placeholder="Enter new password (min 6 characters)" 
//                 value={newPassword} 
//                 onChange={e => {
//                   setNewPassword(e.target.value)
//                   setError('')
//                 }} 
//                 autoFocus
//               />
//             </div>

//             <div className="auth-field">
//               <label>Confirm New Password</label>
//               <input 
//                 type="password" 
//                 placeholder="Confirm your new password" 
//                 value={confirmPassword} 
//                 onChange={e => {
//                   setConfirmPassword(e.target.value)
//                   setError('')
//                 }} 
//               />
//             </div>

//             {error && <p className="auth-error">{error}</p>}
            
//             <button 
//               className="auth-submit" 
//               onClick={handleResetPasswordMobile} 
//               disabled={loading}
//             >
//               {loading ? 'Resetting Password...' : 'Reset Password'}
//             </button>
//           </div>
//         )}

//         {/* Step 4: Success Message */}
//         {step === 'reset' && (
//           <div className="auth-otp-section">
//             <div style={{ textAlign: 'center', marginBottom: '20px' }}>
//               <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
//                 <circle cx="32" cy="32" r="32" fill="#DCFCE7" />
//                 <path d="M20 32L28 40L44 24" stroke="#16A34A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
//               </svg>
//             </div>
            
//             <p style={{ textAlign: 'center', marginBottom: '20px', color: '#10B981' }}>
//               Your password has been successfully reset!
//             </p>
            
//             <button className="auth-submit" onClick={onClose}>
//               Go to Login
//             </button>
//           </div>
//         )}
//       </div>
//     </div>
//   )
// }

// export default ForgotPasswordModal

// import React, { useState, useRef, useEffect } from 'react'
// import { 
//   forgotPassword, 
//   generateOTP,
//   sendMsg91Otp,
//   verifyMsg91Otp,
//   retryMsg91Otp,
//   forgotPasswordMsg91
// } from '../apis/services/pgApi'

// // OTP Input Component with dynamic length
// const OTPInput = ({ value, onChange, length = 6, disabled = false }) => {
//   const inputs = useRef([])

//   const handleKey = (i, e) => {
//     if (e.key === 'Backspace' && !e.target.value && i > 0) {
//       inputs.current[i - 1]?.focus()
//     }
//   }

//   const handleChange = (i, e) => {
//     if (disabled) return
//     const digit = e.target.value.replace(/\D/g, '').slice(-1)
//     const arr = value.split('')
//     arr[i] = digit
//     const next = arr.join('').padEnd(length, ' ').slice(0, length)
//     onChange(next.trimEnd())
//     if (digit && i < length - 1) inputs.current[i + 1]?.focus()
//   }

//   const handlePaste = (e) => {
//     if (disabled) return
//     const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
//     if (paste) {
//       onChange(paste)
//       inputs.current[Math.min(paste.length, length - 1)]?.focus()
//     }
//     e.preventDefault()
//   }

//   return (
//     <div className="otp-row">
//       {Array.from({ length: length }, (_, i) => (
//         <input
//           key={i}
//           ref={el => inputs.current[i] = el}
//           type="text"
//           inputMode="numeric"
//           maxLength={1}
//           className={`otp-box ${value[i] && value[i] !== ' ' ? 'filled' : ''} ${disabled ? 'disabled' : ''}`}
//           value={value[i] && value[i] !== ' ' ? value[i] : ''}
//           onChange={e => handleChange(i, e)}
//           onKeyDown={e => handleKey(i, e)}
//           onPaste={handlePaste}
//           disabled={disabled}
//         />
//       ))}
//     </div>
//   )
// }

// const ForgotPasswordModal = ({ onClose }) => {
//   const overlayRef = useRef(null)
//   const [step, setStep] = useState('form') // 'form', 'otp', 'password', 'reset'
//   const [contact, setContact] = useState('')
//   const [contactType, setContactType] = useState('mobile') // 'mobile' or 'email'
//   const [otp, setOtp] = useState('')
//   const [newPassword, setNewPassword] = useState('')
//   const [confirmPassword, setConfirmPassword] = useState('')
//   const [error, setError] = useState('')
//   const [loading, setLoading] = useState(false)
//   const [generatedOTP, setGeneratedOTP] = useState('')
//   const [otpVerified, setOtpVerified] = useState(false)
//   const [otpLength, setOtpLength] = useState(4) // Default 6 for email

//   useEffect(() => {
//     document.body.style.overflow = 'hidden'
//     return () => { document.body.style.overflow = '' }
//   }, [])

//   useEffect(() => {
//     const h = (e) => { if (e.key === 'Escape') onClose() }
//     window.addEventListener('keydown', h)
//     return () => window.removeEventListener('keydown', h)
//   }, [onClose])

//   const handleOverlayClick = (e) => { if (e.target === overlayRef.current) onClose() }

//   // Format mobile number with +91
//   const getFormattedMobile = (mobile) => {
//     const cleaned = mobile.replace(/\D/g, '')
//     if (cleaned.length === 10) {
//       return `91${cleaned}`
//     }
//     return cleaned
//   }

//   // Reset everything to form step
//   const resetToForm = () => {
//     setStep('form')
//     setError('')
//     setOtp('')
//     setOtpVerified(false)
//     setNewPassword('')
//     setConfirmPassword('')
//     setLoading(false)
//   }

//   // Handle Send OTP
//   const handleSendOTP = async () => {
//     if (!contact.trim()) {
//       setError('Please enter mobile number or email address')
//       return
//     }

//     // Validate based on contact type
//     if (contactType === 'mobile') {
//       if (!/^\d{10}$/.test(contact.trim())) {
//         setError('Please enter a valid 10-digit mobile number')
//         return
//       }
//     } else {
//       if (!/\S+@\S+\.\S+/.test(contact.trim())) {
//         setError('Please enter a valid email address')
//         return
//       }
//     }

//     setLoading(true)
//     setError('')

//     try {
//       if (contactType === 'mobile') {
//         // ✅ MOBILE: Send OTP via MSG91 (4-digit OTP)
//         const mobileWithCountryCode = getFormattedMobile(contact.trim())
        
//         const otpData = {
//           mobile: mobileWithCountryCode,
//           authkey: '522317Abg4sY6n6a2fb1ebP1',
//           template_id: '6a35297075006cf37e02f3f2',
//           param1: 'OTP',
//           param2: 'Forgot Password',
//           param3: 'PG'
//         }

//         console.log('Calling MSG91 send OTP with:', otpData)
//         const response = await sendMsg91Otp(otpData)
//         console.log('MSG91 Send OTP response:', response)

//         if (response && response.success) {
//           setOtpLength(4)
//           setStep('otp')
//           setOtp('')
//           setOtpVerified(false)
//           setError('')
//         } else {
//           setError(response.message || 'Failed to send OTP. Please try again.')
//         }
//       } else {
//         // ✅ EMAIL: Use existing generateOTP (6-digit OTP)
//         const otpData = {
//           MobileNumber: contact.trim(),
//           Purpose: 'FORGOT_PASSWORD',
//           PGID: null,
//           ReferenceID: null
//         }

//         console.log('Calling generate OTP for email with:', otpData)
//         const response = await generateOTP(otpData)
//         console.log('Generate OTP response:', response)

//         if (response && response.OTPCode) {
//           setGeneratedOTP(response.OTPCode)
//           console.log('OTP Generated:', response.OTPCode)
//           setOtpLength(4)
//           setStep('otp')
//           setOtp('')
//           setError('')
//         } else {
//           setError('Failed to send OTP. Please try again.')
//         }
//       }
//     } catch (err) {
//       console.error('Error sending OTP:', err)
//       setError(err.message || 'Failed to send OTP. Please try again.')
//     } finally {
//       setLoading(false)
//     }
//   }

//   // Handle Verify OTP
//   const handleVerifyOTP = async () => {
//     const cleanedOTP = otp.replace(/\s/g, '')
//     const expectedLength = contactType === 'mobile' ? 4 : 4
    
//     // if (cleanedOTP.length !== expectedLength) {
//     //   setError(`Please enter the ${expectedLength}-digit OTP`)
//     //   return
//     // }

//     setLoading(true)
//     setError('')

//     try {
//       if (contactType === 'mobile') {
//         // ✅ MOBILE: Verify OTP via MSG91
//         const mobileWithCountryCode = getFormattedMobile(contact.trim())
        
//         console.log('Verifying MSG91 OTP for:', mobileWithCountryCode, 'OTP:', cleanedOTP)
//         const response = await verifyMsg91Otp(mobileWithCountryCode, cleanedOTP)
//         console.log('MSG91 Verify response:', response)

//         if (response && response.success) {
//           setOtpVerified(true)
//           setStep('password') // Move to password step
//           setError('')
//         } else {
//           setError(response.message || 'Invalid OTP. Please try again.')
//         }
//       } else {
//         // ✅ EMAIL: OTP is verified during reset
//         // For email, we can also verify separately if needed
//         // For now, move to password step
//         setOtpVerified(true)
//         setStep('password')
//         setError('')
//       }
//     } catch (err) {
//       console.error('Error verifying OTP:', err)
//       setError(err.message || 'OTP verification failed. Please try again.')
//     } finally {
//       setLoading(false)
//     }
//   }

//   // Handle Reset Password
//   const handleResetPassword = async () => {
//     // Validate passwords
//     if (!newPassword.trim()) {
//       setError('Please enter new password')
//       return
//     }

//     if (newPassword.length < 6) {
//       setError('Password must be at least 6 characters long')
//       return
//     }

//     if (newPassword !== confirmPassword) {
//       setError('Passwords do not match')
//       return
//     }

//     setLoading(true)
//     setError('')

//     try {
//       const cleanedOTP = otp.replace(/\s/g, '')

//       if (contactType === 'mobile') {
//         // ✅ MOBILE: Use MSG91 forgot password endpoint
//         const resetData = {
//           MobileNo: contact.trim(),
//           EmailID: null,
//           OTPVerifyByMSG91: 'verified',
//           NewPassword: newPassword
//         }

//         console.log('Calling forgot password MSG91 with:', resetData)
//         const response = await forgotPasswordMsg91(resetData)
//         console.log('Forgot password MSG91 response:', response)

//         if (response && response.success) {
//           setStep('reset')
//           setError('')
//         } else {
//           setError(response.message || 'Failed to reset password. Please try again.')
//         }
//       } else {
//         // ✅ EMAIL: Use existing forgot password
//         const resetData = {
//           MobileNo: null,
//           EmailID: contact.trim(),
//           OTP: cleanedOTP,
//           NewPassword: newPassword
//         }

//         console.log('Calling forgot password for email with:', resetData)
//         const response = await forgotPassword(resetData)
//         console.log('Forgot password email response:', response)

//         if (response && response.success) {
//           setStep('reset')
//           setError('')
//         } else {
//           setError(response.message || 'Failed to reset password. Please try again.')
//         }
//       }
//     } catch (err) {
//       console.error('Error resetting password:', err)
//       setError(err.message || 'Failed to reset password. Please try again.')
//     } finally {
//       setLoading(false)
//     }
//   }

//   // Resend OTP
//   const handleResendOTP = async () => {
//     setLoading(true)
//     setError('')

//     try {
//       if (contactType === 'mobile') {
//         // ✅ MOBILE: Resend OTP via MSG91
//         const mobileWithCountryCode = getFormattedMobile(contact.trim())
//         const response = await retryMsg91Otp(mobileWithCountryCode, 'text')
//         console.log('MSG91 Retry response:', response)

//         if (response && response.success) {
//           setOtp('')
//           setOtpVerified(false)
//           setStep('otp')
//           setError('OTP resent successfully!')
//           setTimeout(() => setError(''), 3000)
//         } else {
//           setError(response.message || 'Failed to resend OTP. Please try again.')
//         }
//       } else {
//         // ✅ EMAIL: Resend OTP via existing service
//         const otpData = {
//           MobileNumber: contact.trim(),
//           Purpose: 'FORGOT_PASSWORD',
//           PGID: null,
//           ReferenceID: null
//         }

//         const response = await generateOTP(otpData)
//         console.log('OTP Resent:', response)

//         if (response && response.OTPCode) {
//           setGeneratedOTP(response.OTPCode)
//           setOtp('')
//           setOtpVerified(false)
//           setStep('otp')
//           setError('OTP resent successfully!')
//           setTimeout(() => setError(''), 3000)
//         } else {
//           setError('Failed to resend OTP. Please try again.')
//         }
//       }
//     } catch (err) {
//       console.error('Error resending OTP:', err)
//       setError('Failed to resend OTP. Please try again.')
//     } finally {
//       setLoading(false)
//     }
//   }

//   // Handle OTP input change
//   const handleOtpChange = (value) => {
//     setOtp(value)
//     setError('')
    
//     // Auto-verify when OTP is complete (only for mobile with 4 digits)
//     const cleanedOTP = value.replace(/\s/g, '')
//     if (contactType === 'mobile' && cleanedOTP.length === 4 && !otpVerified) {
//       // Small delay to let user finish typing
//       setTimeout(() => {
//         handleVerifyOTP()
//       }, 500)
//     }
//   }

//   // Get OTP length display message
//   const getOTPLengthMessage = () => {
//     // return contactType === 'mobile' ? '4-digit' : '6-digit'
//     return '4-digit'
//   }

//   // Handle back button click
//   const handleBack = () => {
//     if (step === 'form') {
//       onClose() // Close modal if on form step
//     } else if (step === 'otp') {
//       // Go back to form (send OTP step)
//       resetToForm()
//     } else if (step === 'password') {
//       // Go back to form (send OTP step)
//       resetToForm()
//     } else if (step === 'reset') {
//       // Go back to form
//       resetToForm()
//     }
//   }

//   return (
//     <div className="auth-overlay" ref={overlayRef} onClick={handleOverlayClick}>
//       <div className="auth-modal">
//         <button className="auth-close" onClick={onClose}>
//           <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
//             <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
//           </svg>
//         </button>

//         {/* Back button - goes back to form step */}
//         {step !== 'form' && (
//           <button className="auth-back" onClick={handleBack}>
//             <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
//               <path d="M13 4L7 10L13 16" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
//             </svg>
//             Back
//           </button>
//         )}

//         <div className="auth-header">
//           {step === 'form' && (
//             <>
//               <h2>Forgot Password?</h2>
//               <p>Enter your registered mobile number or email to reset password</p>
//             </>
//           )}
//           {step === 'otp' && (
//             <>
//               <h2>Enter OTP</h2>
//               <p>We've sent a {getOTPLengthMessage()} verification code to your {contactType}</p>
//               {contactType === 'mobile' && (
//                 <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
//                   OTP will be auto-verified when you complete all 4 digits
//                 </p>
//               )}
//             </>
//           )}
//           {step === 'password' && (
//             <>
//               <h2>✓ OTP Verified!</h2>
//               <p>Now set your new password</p>
//             </>
//           )}
//           {step === 'reset' && (
//             <>
//               <h2>Password Reset Successfully!</h2>
//               <p>You can now login with your new password</p>
//             </>
//           )}
//         </div>

//         {/* Step 1: Contact Input */}
//         {step === 'form' && (
//           <div className="auth-form">
//             <div className="auth-type-toggle" style={{ marginBottom: '20px' }}>
//               <button 
//                 className={contactType === 'mobile' ? 'active' : ''} 
//                 onClick={() => {
//                   setContactType('mobile')
//                   setContact('')
//                   setError('')
//                 }}
//               >
//                 Using Mobile Number
//               </button>
//               <button 
//                 className={contactType === 'email' ? 'active' : ''} 
//                 onClick={() => {
//                   setContactType('email')
//                   setContact('')
//                   setError('')
//                 }}
//               >
//                 Using Email ID
//               </button>
//             </div>

//             <div className="auth-field">
//               <label>{contactType === 'mobile' ? 'Mobile Number' : 'Email Address'}</label>
//               {contactType === 'mobile' ? (
//                 <div className="auth-phone-row">
//                   <span className="auth-phone-prefix">+91</span>
//                   <input 
//                     type="tel" 
//                     placeholder="Enter 10-digit mobile number" 
//                     value={contact} 
//                     onChange={e => {
//                       const value = e.target.value.replace(/\D/g, '').slice(0, 10)
//                       setContact(value)
//                       setError('')
//                     }} 
//                     maxLength={10} 
//                     autoFocus
//                   />
//                 </div>
//               ) : (
//                 <input 
//                   type="email" 
//                   placeholder="you@example.com" 
//                   value={contact} 
//                   onChange={e => {
//                     setContact(e.target.value)
//                     setError('')
//                   }} 
//                   autoFocus
//                 />
//               )}
//             </div>

//             {error && <p className="auth-error">{error}</p>}
            
//             <button 
//               className="auth-submit" 
//               onClick={handleSendOTP} 
//               disabled={loading}
//             >
//               {loading ? 'Sending OTP...' : 'Send OTP'}
//             </button>
//           </div>
//         )}

//         {/* Step 2: OTP Input */}
//         {step === 'otp' && (
//           <div className="auth-otp-section">
//             <p className="auth-otp-hint" style={{ textAlign: 'center', marginBottom: '15px' }}>
//               Enter the {getOTPLengthMessage()} verification code sent to your {contactType}
//             </p>
            
//             <OTPInput 
//               value={otp} 
//               onChange={handleOtpChange} 
//               // length={contactType === 'mobile' ? 4 : 6}
//               length={4}
//               disabled={loading || otpVerified}
//             />
            
//             {loading && (
//               <p style={{ color: '#6366F1', textAlign: 'center', marginTop: '10px', fontSize: '14px' }}>
//                 Verifying OTP...
//               </p>
//             )}
            
//             <p className="auth-otp-hint" style={{ marginTop: '15px' }}>
//               Didn't receive it?{' '}
//               <span className="auth-resend" onClick={handleResendOTP}>
//                 Resend Code
//               </span>
//             </p>

//             {/* Manual Verify button for mobile (visible when OTP is complete but not verified) */}
//             {contactType === 'mobile' && !otpVerified && !loading && otp.replace(/\s/g, '').length === 4 && (
//               <button 
//                 className="auth-submit" 
//                 onClick={handleVerifyOTP} 
//                 disabled={loading}
//                 style={{ marginTop: '15px', backgroundColor: '#6366F1' }}
//               >
//                 Verify OTP
//               </button>
//             )}

//             {error && <p className="auth-error" style={{ marginTop: '15px' }}>{error}</p>}
//           </div>
//         )}

//         {/* Step 3: New Password */}
//         {step === 'password' && (
//           <div className="auth-otp-section">
//             <div style={{ 
//               textAlign: 'center', 
//               marginBottom: '20px',
//               padding: '10px',
//               backgroundColor: '#DCFCE7',
//               borderRadius: '8px',
//               color: '#16A34A'
//             }}>
//               ✓ OTP verified successfully!
//             </div>

//             <div className="auth-field">
//               <label>New Password</label>
//               <input 
//                 type="password" 
//                 placeholder="Enter new password (min 6 characters)" 
//                 value={newPassword} 
//                 onChange={e => {
//                   setNewPassword(e.target.value)
//                   setError('')
//                 }} 
//                 autoFocus
//               />
//             </div>

//             <div className="auth-field">
//               <label>Confirm New Password</label>
//               <input 
//                 type="password" 
//                 placeholder="Confirm your new password" 
//                 value={confirmPassword} 
//                 onChange={e => {
//                   setConfirmPassword(e.target.value)
//                   setError('')
//                 }} 
//               />
//             </div>

//             {error && <p className="auth-error">{error}</p>}
            
//             <button 
//               className="auth-submit" 
//               onClick={handleResetPassword} 
//               disabled={loading}
//             >
//               {loading ? 'Resetting Password...' : 'Reset Password'}
//             </button>
//           </div>
//         )}

//         {/* Step 4: Success Message */}
//         {step === 'reset' && (
//           <div className="auth-otp-section">
//             <div style={{ textAlign: 'center', marginBottom: '20px' }}>
//               <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
//                 <circle cx="32" cy="32" r="32" fill="#DCFCE7" />
//                 <path d="M20 32L28 40L44 24" stroke="#16A34A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
//               </svg>
//             </div>
            
//             <p style={{ textAlign: 'center', marginBottom: '20px', color: '#10B981' }}>
//               Your password has been successfully reset!
//             </p>
            
//             <button className="auth-submit" onClick={onClose}>
//               Go to Login
//             </button>
//           </div>
//         )}
//       </div>
//     </div>
//   )
// }

// export default ForgotPasswordModal

// import React, { useState, useRef, useEffect } from 'react'
// import { 
//   forgotPassword, 
//   generateOTP,
//   sendMsg91Otp,
//   verifyMsg91Otp,
//   retryMsg91Otp,
//   forgotPasswordMsg91
// } from '../apis/services/pgApi'

// // OTP Input Component with dynamic length
// const OTPInput = ({ value, onChange, length = 6 }) => {
//   const inputs = useRef([])

//   const handleKey = (i, e) => {
//     if (e.key === 'Backspace' && !e.target.value && i > 0) {
//       inputs.current[i - 1]?.focus()
//     }
//   }

//   const handleChange = (i, e) => {
//     const digit = e.target.value.replace(/\D/g, '').slice(-1)
//     const arr = value.split('')
//     arr[i] = digit
//     const next = arr.join('').padEnd(length, ' ').slice(0, length)
//     onChange(next.trimEnd())
//     if (digit && i < length - 1) inputs.current[i + 1]?.focus()
//   }

//   const handlePaste = (e) => {
//     const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
//     if (paste) {
//       onChange(paste)
//       inputs.current[Math.min(paste.length, length - 1)]?.focus()
//     }
//     e.preventDefault()
//   }

//   return (
//     <div className="otp-row">
//       {Array.from({ length: length }, (_, i) => (
//         <input
//           key={i}
//           ref={el => inputs.current[i] = el}
//           type="text"
//           inputMode="numeric"
//           maxLength={1}
//           className={`otp-box ${value[i] && value[i] !== ' ' ? 'filled' : ''}`}
//           value={value[i] && value[i] !== ' ' ? value[i] : ''}
//           onChange={e => handleChange(i, e)}
//           onKeyDown={e => handleKey(i, e)}
//           onPaste={handlePaste}
//         />
//       ))}
//     </div>
//   )
// }

// const ForgotPasswordModal = ({ onClose }) => {
//   const overlayRef = useRef(null)
//   const [step, setStep] = useState('form') // 'form', 'otp', 'reset'
//   const [contact, setContact] = useState('')
//   const [contactType, setContactType] = useState('mobile') // 'mobile' or 'email'
//   const [otp, setOtp] = useState('')
//   const [newPassword, setNewPassword] = useState('')
//   const [confirmPassword, setConfirmPassword] = useState('')
//   const [error, setError] = useState('')
//   const [loading, setLoading] = useState(false)
//   const [generatedOTP, setGeneratedOTP] = useState('')
//   const [otpVerified, setOtpVerified] = useState(false)
//   const [otpLength, setOtpLength] = useState(6) // Default 6 for email

//   useEffect(() => {
//     document.body.style.overflow = 'hidden'
//     return () => { document.body.style.overflow = '' }
//   }, [])

//   useEffect(() => {
//     const h = (e) => { if (e.key === 'Escape') onClose() }
//     window.addEventListener('keydown', h)
//     return () => window.removeEventListener('keydown', h)
//   }, [onClose])

//   const handleOverlayClick = (e) => { if (e.target === overlayRef.current) onClose() }

//   // Handle Send OTP
//   const handleSendOTP = async () => {
//     if (!contact.trim()) {
//       setError('Please enter mobile number or email address')
//       return
//     }

//     // Validate based on contact type
//     if (contactType === 'mobile') {
//       if (!/^\d{10}$/.test(contact.trim())) {
//         setError('Please enter a valid 10-digit mobile number')
//         return
//       }
//     } else {
//       if (!/\S+@\S+\.\S+/.test(contact.trim())) {
//         setError('Please enter a valid email address')
//         return
//       }
//     }

//     setLoading(true)
//     setError('')

//     try {
//       if (contactType === 'mobile') {
//         // ✅ MOBILE: Send OTP via MSG91 (4-digit OTP)
//         const otpData = {
//           mobile: contact.trim(),
//           authkey: '522317Abg4sY6n6a2fb1ebP1',
//           template_id: '6a35297075006cf37e02f3f2',
//           param1: 'OTP',
//           param2: 'Forgot Password',
//           param3: 'PG'
//         }

//         console.log('Calling MSG91 send OTP with:', otpData)
//         const response = await sendMsg91Otp(otpData)
//         console.log('MSG91 Send OTP response:', response)

//         if (response && response.success) {
//           setOtpLength(4) // MSG91 sends 4-digit OTP
//           setStep('otp')
//           setOtp('')
//           setOtpVerified(false)
//           setError('')
//         } else {
//           setError(response.message || 'Failed to send OTP. Please try again.')
//         }
//       } else {
//         // ✅ EMAIL: Use existing generateOTP (6-digit OTP)
//         const otpData = {
//           MobileNumber: contact.trim(),
//           Purpose: 'FORGOT_PASSWORD',
//           PGID: null,
//           ReferenceID: null
//         }

//         console.log('Calling generate OTP for email with:', otpData)
//         const response = await generateOTP(otpData)
//         console.log('Generate OTP response:', response)

//         if (response && response.OTPCode) {
//           setGeneratedOTP(response.OTPCode)
//           console.log('OTP Generated:', response.OTPCode)
//           setOtpLength(6) // Email OTP is 6-digit
//           setStep('otp')
//           setOtp('')
//           setError('')
//         } else {
//           setError('Failed to send OTP. Please try again.')
//         }
//       }
//     } catch (err) {
//       console.error('Error sending OTP:', err)
//       setError(err.message || 'Failed to send OTP. Please try again.')
//     } finally {
//       setLoading(false)
//     }
//   }

//   // Handle Verify OTP (only for mobile MSG91)
//   const handleVerifyOTP = async () => {
//     const cleanedOTP = otp.replace(/\s/g, '')
//     const expectedLength = contactType === 'mobile' ? 4 : 6
    
//     if (cleanedOTP.length !== expectedLength) {
//       setError(`Please enter the ${expectedLength}-digit OTP`)
//       return
//     }

//     setLoading(true)
//     setError('')

//     try {
//       // ✅ MOBILE: Verify OTP via MSG91
//       console.log('Verifying MSG91 OTP for:', contact.trim(), 'OTP:', cleanedOTP)
//       const response = await verifyMsg91Otp(contact.trim(), cleanedOTP)
//       console.log('MSG91 Verify response:', response)

//       if (response && response.success) {
//         setOtpVerified(true)
//         setError('OTP verified successfully!')
//         setTimeout(() => setError(''), 2000)
//       } else {
//         setError(response.message || 'Invalid OTP. Please try again.')
//       }
//     } catch (err) {
//       console.error('Error verifying OTP:', err)
//       setError(err.message || 'OTP verification failed. Please try again.')
//     } finally {
//       setLoading(false)
//     }
//   }

//   // Handle Reset Password
//   const handleResetPassword = async () => {
//     // Validate OTP
//     const cleanedOTP = otp.replace(/\s/g, '')
//     const expectedLength = contactType === 'mobile' ? 4 : 6
    
//     if (cleanedOTP.length !== expectedLength) {
//       setError(`Please enter the ${expectedLength}-digit OTP`)
//       return
//     }

//     // For mobile, OTP must be verified first
//     if (contactType === 'mobile' && !otpVerified) {
//       setError('Please verify your OTP first')
//       return
//     }

//     // Validate passwords
//     if (!newPassword.trim()) {
//       setError('Please enter new password')
//       return
//     }

//     if (newPassword.length < 6) {
//       setError('Password must be at least 6 characters long')
//       return
//     }

//     if (newPassword !== confirmPassword) {
//       setError('Passwords do not match')
//       return
//     }

//     setLoading(true)
//     setError('')

//     try {
//       if (contactType === 'mobile') {
//         // ✅ MOBILE: Use MSG91 forgot password endpoint
//         const resetData = {
//           MobileNo: contact.trim(),
//           EmailID: null,
//           OTPVerifyByMSG91: otpVerified ? 'verified' : 'notverified',
//           NewPassword: newPassword
//         }

//         console.log('Calling forgot password MSG91 with:', resetData)
//         const response = await forgotPasswordMsg91(resetData)
//         console.log('Forgot password MSG91 response:', response)

//         if (response && response.success) {
//           setStep('reset')
//           setError('')
//         } else {
//           setError(response.message || 'Failed to reset password. Please try again.')
//         }
//       } else {
//         // ✅ EMAIL: Use existing forgot password
//         const resetData = {
//           MobileNo: null,
//           EmailID: contact.trim(),
//           OTP: cleanedOTP,
//           NewPassword: newPassword
//         }

//         console.log('Calling forgot password for email with:', resetData)
//         const response = await forgotPassword(resetData)
//         console.log('Forgot password email response:', response)

//         if (response && response.success) {
//           setStep('reset')
//           setError('')
//         } else {
//           setError(response.message || 'Failed to reset password. Please try again.')
//         }
//       }
//     } catch (err) {
//       console.error('Error resetting password:', err)
//       setError(err.message || 'Failed to reset password. Please try again.')
//     } finally {
//       setLoading(false)
//     }
//   }

//   // Resend OTP
//   const handleResendOTP = async () => {
//     setLoading(true)
//     setError('')

//     try {
//       if (contactType === 'mobile') {
//         // ✅ MOBILE: Resend OTP via MSG91
//         const response = await retryMsg91Otp(contact.trim(), 'text')
//         console.log('MSG91 Retry response:', response)

//         if (response && response.success) {
//           setOtp('')
//           setOtpVerified(false)
//           setError('OTP resent successfully!')
//           setTimeout(() => setError(''), 3000)
//         } else {
//           setError(response.message || 'Failed to resend OTP. Please try again.')
//         }
//       } else {
//         // ✅ EMAIL: Resend OTP via existing service
//         const otpData = {
//           MobileNumber: contact.trim(),
//           Purpose: 'FORGOT_PASSWORD',
//           PGID: null,
//           ReferenceID: null
//         }

//         const response = await generateOTP(otpData)
//         console.log('OTP Resent:', response)

//         if (response && response.OTPCode) {
//           setGeneratedOTP(response.OTPCode)
//           setOtp('')
//           setError('OTP resent successfully!')
//           setTimeout(() => setError(''), 3000)
//         } else {
//           setError('Failed to resend OTP. Please try again.')
//         }
//       }
//     } catch (err) {
//       console.error('Error resending OTP:', err)
//       setError('Failed to resend OTP. Please try again.')
//     } finally {
//       setLoading(false)
//     }
//   }

//   // Handle OTP input change
//   const handleOtpChange = (value) => {
//     setOtp(value)
//     setError('')
    
//     // Auto-verify when OTP is complete (only for mobile with 4 digits)
//     const cleanedOTP = value.replace(/\s/g, '')
//     if (contactType === 'mobile' && cleanedOTP.length === 4 && !otpVerified) {
//       // Small delay to let user finish typing
//       setTimeout(() => {
//         handleVerifyOTP()
//       }, 500)
//     }
//   }

//   // Get OTP length display message
//   const getOTPLengthMessage = () => {
//     return contactType === 'mobile' ? '4-digit' : '6-digit'
//   }

//   return (
//     <div className="auth-overlay" ref={overlayRef} onClick={handleOverlayClick}>
//       <div className="auth-modal">
//         <button className="auth-close" onClick={onClose}>
//           <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
//             <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
//           </svg>
//         </button>

//         {/* Back button */}
//         {step !== 'form' && (
//           <button className="auth-back" onClick={() => { 
//             setStep('form')
//             setError('')
//             setOtp('')
//             setOtpVerified(false)
//             setNewPassword('')
//             setConfirmPassword('')
//           }}>
//             <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
//               <path d="M13 4L7 10L13 16" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
//             </svg>
//             Back
//           </button>
//         )}

//         <div className="auth-header">
//           {step === 'form' && (
//             <>
//               <h2>Forgot Password?</h2>
//               <p>Enter your registered mobile number or email to reset password</p>
//             </>
//           )}
//           {step === 'otp' && (
//             <>
//               <h2>Enter OTP</h2>
//               <p>We've sent a {getOTPLengthMessage()} verification code to your {contactType}</p>
//               {contactType === 'mobile' && (
//                 <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
//                   OTP will be auto-verified when you complete all 4 digits
//                 </p>
//               )}
//               {contactType === 'email' && (
//                 <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
//                   Enter the 6-digit OTP sent to your email
//                 </p>
//               )}
//             </>
//           )}
//           {step === 'reset' && (
//             <>
//               <h2>Password Reset Successfully!</h2>
//               <p>You can now login with your new password</p>
//             </>
//           )}
//         </div>

//         {/* Step 1: Contact Input */}
//         {step === 'form' && (
//           <div className="auth-form">
//             <div className="auth-type-toggle" style={{ marginBottom: '20px' }}>
//               <button 
//                 className={contactType === 'mobile' ? 'active' : ''} 
//                 onClick={() => {
//                   setContactType('mobile')
//                   setContact('')
//                   setError('')
//                 }}
//               >
//                 Using Mobile Number
//               </button>
//               <button 
//                 className={contactType === 'email' ? 'active' : ''} 
//                 onClick={() => {
//                   setContactType('email')
//                   setContact('')
//                   setError('')
//                 }}
//               >
//                 Using Email ID
//               </button>
//             </div>

//             <div className="auth-field">
//               <label>{contactType === 'mobile' ? 'Mobile Number' : 'Email Address'}</label>
//               {contactType === 'mobile' ? (
//                 <div className="auth-phone-row">
//                   <span className="auth-phone-prefix">+91</span>
//                   <input 
//                     type="tel" 
//                     placeholder="10-digit mobile number" 
//                     value={contact} 
//                     onChange={e => {
//                       setContact(e.target.value.replace(/\D/g, ''))
//                       setError('')
//                     }} 
//                     maxLength={10} 
//                     autoFocus
//                   />
//                 </div>
//               ) : (
//                 <input 
//                   type="email" 
//                   placeholder="you@example.com" 
//                   value={contact} 
//                   onChange={e => {
//                     setContact(e.target.value)
//                     setError('')
//                   }} 
//                   autoFocus
//                 />
//               )}
//             </div>

//             {error && <p className="auth-error">{error}</p>}
            
//             <button 
//               className="auth-submit" 
//               onClick={handleSendOTP} 
//               disabled={loading}
//             >
//               {loading ? 'Sending OTP...' : 'Send OTP'}
//             </button>
//           </div>
//         )}

//         {/* Step 2: OTP and New Password */}
//         {step === 'otp' && (
//           <div className="auth-otp-section">
//             <p className="auth-otp-hint" style={{ textAlign: 'center', marginBottom: '15px' }}>
//               Enter the {getOTPLengthMessage()} verification code sent to your {contactType}
//             </p>
            
//             <OTPInput 
//               value={otp} 
//               onChange={handleOtpChange} 
//               length={contactType === 'mobile' ? 4 : 6}
//             />
            
//             {/* Show verification status for mobile */}
//             {contactType === 'mobile' && otpVerified && (
//               <p style={{ color: '#10B981', textAlign: 'center', marginTop: '10px', fontSize: '14px', fontWeight: 500 }}>
//                 ✓ OTP Verified Successfully
//               </p>
//             )}
            
//             <p className="auth-otp-hint">
//               Didn't receive it?{' '}
//               <span className="auth-resend" onClick={handleResendOTP}>
//                 Resend Code
//               </span>
//             </p>

//             {/* Manual Verify button for mobile (visible when OTP is complete but not verified) */}
//             {contactType === 'mobile' && !otpVerified && otp.replace(/\s/g, '').length === 4 && (
//               <button 
//                 className="auth-submit" 
//                 onClick={handleVerifyOTP} 
//                 disabled={loading}
//                 style={{ marginTop: '10px', backgroundColor: '#6366F1' }}
//               >
//                 {loading ? 'Verifying...' : 'Verify OTP'}
//               </button>
//             )}

//             <div className="auth-field" style={{ marginTop: '20px' }}>
//               <label>New Password</label>
//               <input 
//                 type="password" 
//                 placeholder="Enter new password (min 6 characters)" 
//                 value={newPassword} 
//                 onChange={e => {
//                   setNewPassword(e.target.value)
//                   setError('')
//                 }} 
//               />
//             </div>

//             <div className="auth-field">
//               <label>Confirm New Password</label>
//               <input 
//                 type="password" 
//                 placeholder="Confirm your new password" 
//                 value={confirmPassword} 
//                 onChange={e => {
//                   setConfirmPassword(e.target.value)
//                   setError('')
//                 }} 
//               />
//             </div>

//             {error && <p className={`auth-error ${error.includes('successfully') ? 'auth-success' : ''}`}>{error}</p>}
            
//             <button 
//               className="auth-submit" 
//               onClick={handleResetPassword} 
//               disabled={loading || (contactType === 'mobile' && !otpVerified)}
//             >
//               {loading ? 'Resetting Password...' : 'Reset Password'}
//             </button>

//             {contactType === 'mobile' && !otpVerified && otp.replace(/\s/g, '').length === 4 && (
//               <p style={{ fontSize: '12px', color: '#F59E0B', textAlign: 'center', marginTop: '8px' }}>
//                 ⚠️ Please click "Verify OTP" button to verify before resetting password
//               </p>
//             )}
//           </div>
//         )}

//         {/* Step 3: Success Message */}
//         {step === 'reset' && (
//           <div className="auth-otp-section">
//             <div style={{ textAlign: 'center', marginBottom: '20px' }}>
//               <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
//                 <circle cx="32" cy="32" r="32" fill="#DCFCE7" />
//                 <path d="M20 32L28 40L44 24" stroke="#16A34A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
//               </svg>
//             </div>
            
//             <p style={{ textAlign: 'center', marginBottom: '20px', color: '#10B981' }}>
//               Your password has been successfully reset!
//             </p>
            
//             <button className="auth-submit" onClick={onClose}>
//               Go to Login
//             </button>
//           </div>
//         )}
//       </div>
//     </div>
//   )
// }

// export default ForgotPasswordModal



// import React, { useState, useRef, useEffect } from 'react'
// import { forgotPassword, generateOTP } from '../apis/services/pgApi'

// // OTP Input Component (reuse from your AuthModal)
// const OTPInput = ({ value, onChange }) => {
//   const inputs = useRef([])

//   const handleKey = (i, e) => {
//     if (e.key === 'Backspace' && !e.target.value && i > 0) {
//       inputs.current[i - 1]?.focus()
//     }
//   }

//   const handleChange = (i, e) => {
//     const digit = e.target.value.replace(/\D/g, '').slice(-1)
//     const arr = value.split('')
//     arr[i] = digit
//     const next = arr.join('').padEnd(6, ' ').slice(0, 6)
//     onChange(next.trimEnd())
//     if (digit && i < 5) inputs.current[i + 1]?.focus()
//   }

//   const handlePaste = (e) => {
//     const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
//     if (paste) {
//       onChange(paste)
//       inputs.current[Math.min(paste.length, 5)]?.focus()
//     }
//     e.preventDefault()
//   }

//   return (
//     <div className="otp-row">
//       {Array.from({ length: 6 }, (_, i) => (
//         <input
//           key={i}
//           ref={el => inputs.current[i] = el}
//           type="text"
//           inputMode="numeric"
//           maxLength={1}
//           className={`otp-box ${value[i] && value[i] !== ' ' ? 'filled' : ''}`}
//           value={value[i] && value[i] !== ' ' ? value[i] : ''}
//           onChange={e => handleChange(i, e)}
//           onKeyDown={e => handleKey(i, e)}
//           onPaste={handlePaste}
//         />
//       ))}
//     </div>
//   )
// }

// const ForgotPasswordModal = ({ onClose }) => {
//   const overlayRef = useRef(null)
//   const [step, setStep] = useState('form') // 'form', 'otp', 'reset'
//   const [contact, setContact] = useState('')
//   const [contactType, setContactType] = useState('mobile') // 'mobile' or 'email'
//   const [otp, setOtp] = useState('')
//   const [newPassword, setNewPassword] = useState('')
//   const [confirmPassword, setConfirmPassword] = useState('')
//   const [error, setError] = useState('')
//   const [loading, setLoading] = useState(false)
//   const [generatedOTP, setGeneratedOTP] = useState('')

//   useEffect(() => {
//     document.body.style.overflow = 'hidden'
//     return () => { document.body.style.overflow = '' }
//   }, [])

//   useEffect(() => {
//     const h = (e) => { if (e.key === 'Escape') onClose() }
//     window.addEventListener('keydown', h)
//     return () => window.removeEventListener('keydown', h)
//   }, [onClose])

//   const handleOverlayClick = (e) => { if (e.target === overlayRef.current) onClose() }

//   // Handle Send OTP
//   const handleSendOTP = async () => {
//     if (!contact.trim()) {
//       setError('Please enter mobile number or email address')
//       return
//     }

//     // Validate based on contact type
//     if (contactType === 'mobile') {
//       if (!/^\d{10}$/.test(contact.trim())) {
//         setError('Please enter a valid 10-digit mobile number')
//         return
//       }
//     } else {
//       if (!/\S+@\S+\.\S+/.test(contact.trim())) {
//         setError('Please enter a valid email address')
//         return
//       }
//     }

//     setLoading(true)
//     setError('')

//     try {
//       // Prepare data for generate OTP API
//       const otpData = {
//         MobileNumber: contactType === 'mobile' ? contact.trim() : contact.trim() ,
//         Purpose: 'FORGOT_PASSWORD',
//         PGID: null,
//         ReferenceID: null
//       }

//       console.log('Calling generate OTP with:', otpData)
//       const response = await generateOTP(otpData)
//       console.log('Generate OTP response:', response)

//       // The OTP will be logged in console by your backend
//       // For development, you can also show it in UI
//       if (response && response.OTPCode) {
//         setGeneratedOTP(response.OTPCode)
//         console.log('OTP Generated:', response.OTPCode)
//       }

//       setStep('otp')
//       setOtp('')
//       setError('')
//     } catch (err) {
//       console.error('Error sending OTP:', err)
//       setError(err.message || 'Failed to send OTP. Please try again.')
//     } finally {
//       setLoading(false)
//     }
//   }

//   // Handle Reset Password
//   const handleResetPassword = async () => {
//     // Validate OTP
//     const cleanedOTP = otp.replace(/\s/g, '')
//     if (cleanedOTP.length !== 6) {
//       setError('Please enter the 6-digit OTP')
//       return
//     }

//     // Validate passwords
//     if (!newPassword.trim()) {
//       setError('Please enter new password')
//       return
//     }

//     if (newPassword.length < 6) {
//       setError('Password must be at least 6 characters long')
//       return
//     }

//     if (newPassword !== confirmPassword) {
//       setError('Passwords do not match')
//       return
//     }

//     setLoading(true)
//     setError('')

//     try {
//       const resetData = {
//         MobileNo: contactType === 'mobile' ? contact.trim() : null,
//         EmailID: contactType === 'email' ? contact.trim() : null,
//         OTP: cleanedOTP,
//         NewPassword: newPassword
//       }

//       console.log('Calling forgot password with:', resetData)
//       const response = await forgotPassword(resetData)
//       console.log('Forgot password response:', response)

//       if (response && response.success) {
//         setStep('reset')
//         setError('')
//       } else {
//         setError(response.message || 'Failed to reset password. Please try again.')
//       }
//     } catch (err) {
//       console.error('Error resetting password:', err)
//       setError(err.message || 'Failed to reset password. Please try again.')
//     } finally {
//       setLoading(false)
//     }
//   }

//   // Resend OTP
//   const handleResendOTP = async () => {
//     setLoading(true)
//     setError('')

//     try {
//       const otpData = {
//         MobileNumber: contactType === 'mobile' ? contact.trim() : contact.trim() ,
//         Purpose: 'FORGOT_PASSWORD',
//         PGID: null,
//         ReferenceID: null
//       }

//       const response = await generateOTP(otpData)
//       console.log('OTP Resent:', response)

//       if (response && response.OTPCode) {
//         setGeneratedOTP(response.OTPCode)
//       }

//       setOtp('')
//       setError('OTP resent successfully!')
//       setTimeout(() => setError(''), 3000)
//     } catch (err) {
//       setError('Failed to resend OTP. Please try again.')
//     } finally {
//       setLoading(false)
//     }
//   }

//   return (
//     <div className="auth-overlay" ref={overlayRef} onClick={handleOverlayClick}>
//       <div className="auth-modal">
//         <button className="auth-close" onClick={onClose}>
//           <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
//             <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
//           </svg>
//         </button>

//         {/* Back button */}
//         {step !== 'form' && (
//           <button className="auth-back" onClick={() => { 
//             setStep('form')
//             setError('')
//             setOtp('')
//             setNewPassword('')
//             setConfirmPassword('')
//           }}>
//             <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
//               <path d="M13 4L7 10L13 16" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
//             </svg>
//             Back
//           </button>
//         )}

//         <div className="auth-header">
//           {step === 'form' && (
//             <>
//               <h2>Forgot Password?</h2>
//               <p>Enter your registered mobile number or email to reset password</p>
//             </>
//           )}
//           {step === 'otp' && (
//             <>
//               <h2>Enter OTP</h2>
//               <p>We've sent a verification code to your {contactType}</p>
//             </>
//           )}
//           {step === 'reset' && (
//             <>
//               <h2>Password Reset Successfully!</h2>
//               <p>You can now login with your new password</p>
//             </>
//           )}
//         </div>

//         {/* Step 1: Contact Input */}
//         {step === 'form' && (
//           <div className="auth-form">
//             <div className="auth-type-toggle" style={{ marginBottom: '20px' }}>
//               <button 
//                 className={contactType === 'mobile' ? 'active' : ''} 
//                 onClick={() => {
//                   setContactType('mobile')
//                   setContact('')
//                   setError('')
//                 }}
//               >
//                 Using Mobile Number
//               </button>
//               <button 
//                 className={contactType === 'email' ? 'active' : ''} 
//                 onClick={() => {
//                   setContactType('email')
//                   setContact('')
//                   setError('')
//                 }}
//               >
//                 Using Email ID
//               </button>
//             </div>

//             <div className="auth-field">
//               <label>{contactType === 'mobile' ? 'Mobile Number' : 'Email Address'}</label>
//               {contactType === 'mobile' ? (
//                 <div className="auth-phone-row">
//                   <span className="auth-phone-prefix">+91</span>
//                   <input 
//                     type="tel" 
//                     placeholder="10-digit mobile number" 
//                     value={contact} 
//                     onChange={e => {
//                       setContact(e.target.value.replace(/\D/g, ''))
//                       setError('')
//                     }} 
//                     maxLength={10} 
//                     autoFocus
//                   />
//                 </div>
//               ) : (
//                 <input 
//                   type="email" 
//                   placeholder="you@example.com" 
//                   value={contact} 
//                   onChange={e => {
//                     setContact(e.target.value)
//                     setError('')
//                   }} 
//                   autoFocus
//                 />
//               )}
//             </div>

//             {error && <p className="auth-error">{error}</p>}
            
//             <button 
//               className="auth-submit" 
//               onClick={handleSendOTP} 
//               disabled={loading}
//             >
//               {loading ? 'Sending OTP...' : 'Send OTP'}
//             </button>
//           </div>
//         )}

//         {/* Step 2: OTP and New Password */}
//         {step === 'otp' && (
//           <div className="auth-otp-section">
//             <p className="auth-otp-hint" style={{ textAlign: 'center', marginBottom: '15px' }}>
//               Enter the 6-digit verification code sent to your {contactType}
//             </p>
            
//             <OTPInput value={otp} onChange={setOtp} />
            
//             <p className="auth-otp-hint">
//               Didn't receive it?{' '}
//               <span className="auth-resend" onClick={handleResendOTP}>
//                 Resend Code
//               </span>
//             </p>

//             <div className="auth-field" style={{ marginTop: '20px' }}>
//               <label>New Password</label>
//               <input 
//                 type="password" 
//                 placeholder="Enter new password (min 6 characters)" 
//                 value={newPassword} 
//                 onChange={e => {
//                   setNewPassword(e.target.value)
//                   setError('')
//                 }} 
//               />
//             </div>

//             <div className="auth-field">
//               <label>Confirm New Password</label>
//               <input 
//                 type="password" 
//                 placeholder="Confirm your new password" 
//                 value={confirmPassword} 
//                 onChange={e => {
//                   setConfirmPassword(e.target.value)
//                   setError('')
//                 }} 
//               />
//             </div>

//             {error && <p className="auth-error">{error}</p>}
            
//             <button 
//               className="auth-submit" 
//               onClick={handleResetPassword} 
//               disabled={loading}
//             >
//               {loading ? 'Resetting Password...' : 'Reset Password'}
//             </button>
//           </div>
//         )}

//         {/* Step 3: Success Message */}
//         {step === 'reset' && (
//           <div className="auth-otp-section">
//             <div style={{ textAlign: 'center', marginBottom: '20px' }}>
//               <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
//                 <circle cx="32" cy="32" r="32" fill="#DCFCE7" />
//                 <path d="M20 32L28 40L44 24" stroke="#16A34A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
//               </svg>
//             </div>
            
//             <p style={{ textAlign: 'center', marginBottom: '20px', color: '#10B981' }}>
//               Your password has been successfully reset!
//             </p>
            
//             <button className="auth-submit" onClick={onClose}>
//               Go to Login
//             </button>
//           </div>
//         )}
//       </div>
//     </div>
//   )
// }

// export default ForgotPasswordModal